import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Sparkles } from 'lucide-react';
import IntegrationIcon from '../integrations/shared/IntegrationIcon';
import CentralMemoryHub from './CentralMemoryHub';
import WizardInstallSnippet from '../wizard/WizardInstallSnippet';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../../hooks/useAnalytics';
// import { SlideIn, ScaleIn, HoverLift, RotateOnHover, GradientShift } from '../ui/motion-primitives';

// Types
interface Integration {
  id: string;
  name: string;
  icon: string;
  status: 'ready' | 'coming' | 'development';
  landingPath?: string;
}

interface CTAButton {
  text: string;
  href: string;
  onClick?: () => void;
}

interface HeroSectionProps {
  // Content Props
  title?: string;
  subtitle?: string;
  description?: string;
  trustSignal?: string;
  
  // Integration Props
  featuredIntegrations?: Integration[];
  maxIntegrations?: number;
  showMoreCount?: boolean;
  showCentralHub?: boolean;
  onIntegrationClick?: (integration: Integration) => void;
  
  // CTA Props
  primaryCTA?: CTAButton;
  secondaryCTA?: CTAButton;
  showWizardInstall?: boolean;
  
  // Animation Props
  animationDelay?: number;
  enableAnimations?: boolean;
  staggerDelay?: number;
  
  // Style Props
  className?: string;
  variant?: 'default' | 'compact' | 'enterprise';
}

const HeroSection: React.FC<HeroSectionProps> = ({ 
  title,
  subtitle,
  description,
  trustSignal,
  featuredIntegrations = [],
  maxIntegrations = 10,
  showMoreCount = true,
  showCentralHub = false,
  onIntegrationClick,
  primaryCTA,
  secondaryCTA,
  showWizardInstall = false,
  animationDelay = 0.2,
  enableAnimations = true,
  staggerDelay = 0.1,
  variant = 'default',
  className = "",
  ...props
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();

  // Default content with i18n
  const defaultTitle = title || t('hero.title', { ns: 'homepage' });
  const defaultSubtitle = subtitle ?? (t('hero.subtitle', { ns: 'homepage' }) as string);
  const defaultDescription = description || t('hero.description', { ns: 'homepage' });
  const defaultTrustSignal = trustSignal || t('hero.trustSignal', { ns: 'homepage' });
  
  const defaultPrimaryCTA: CTAButton = primaryCTA || {
    text: t('hero.cta.getStarted', { ns: 'homepage' }),
    href: '/signup'
  };
  
  const defaultSecondaryCTA: CTAButton = secondaryCTA || {
    text: t('hero.cta.seeDemo', { ns: 'homepage' }),
    href: '#demo'
  };

  // Handle integration clicks
  const handleIntegrationClick = (integration: Integration) => {
    if (onIntegrationClick) {
      onIntegrationClick(integration);
    } else if (integration.landingPath?.startsWith('mailto:')) {
      window.location.href = integration.landingPath;
    } else if (integration.landingPath) {
      navigate(integration.landingPath);
    } else {
      navigate('/integrations');
    }
  };

  // Handle CTA clicks - use navigate for internal routes
  const handlePrimaryCTAClick = () => {
    if (defaultPrimaryCTA.onClick) {
      defaultPrimaryCTA.onClick();
    } else if (defaultPrimaryCTA.href.startsWith('/') && !defaultPrimaryCTA.href.startsWith('//')) {
      navigate(defaultPrimaryCTA.href);
    } else {
      window.location.href = defaultPrimaryCTA.href;
    }
  };

  const handleSecondaryCTAClick = () => {
    if (defaultSecondaryCTA.onClick) {
      defaultSecondaryCTA.onClick();
    } else if (defaultSecondaryCTA.href.startsWith('#')) {
      const targetElement = document.querySelector(defaultSecondaryCTA.href);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate(defaultSecondaryCTA.href);
      }
    } else if (defaultSecondaryCTA.href.startsWith('/') && !defaultSecondaryCTA.href.startsWith('//')) {
      navigate(defaultSecondaryCTA.href);
    } else {
      window.location.href = defaultSecondaryCTA.href;
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: animationDelay
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  // Prepare integrations display
  const displayedIntegrations = featuredIntegrations.slice(0, maxIntegrations);
  const remainingCount = Math.max(0, featuredIntegrations.length - maxIntegrations);

  // Variant classes
  const variantClasses = {
    default: "py-12 md:py-16 space-y-6 md:space-y-8 px-4 md:px-8",
    compact: "py-8 md:py-12 space-y-4 md:space-y-6 px-4 md:px-6", 
    enterprise: "py-16 md:py-20 space-y-8 md:space-y-10 px-4 md:px-8"
  };

  return (
    <motion.section 
      variants={enableAnimations ? containerVariants : undefined}
      initial={enableAnimations ? "hidden" : undefined}
      animate={enableAnimations ? "visible" : undefined}
      className={`text-center max-w-4xl mx-auto ${variantClasses[variant]} ${className}`}
      {...props}
    >
      {/* Title and Subtitle */}
      <motion.div
        variants={enableAnimations ? itemVariants : undefined}
        className="space-y-6"
      >
        <motion.h1
          className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-foreground px-4 md:px-0 leading-tight"
          initial={enableAnimations ? { opacity: 0, y: 30 } : undefined}
          animate={enableAnimations ? { opacity: 1, y: 0 } : undefined}
          transition={enableAnimations ? { duration: 0.8, ease: "easeOut" } : undefined}
        >
          <motion.span
            className="block bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent"
            initial={enableAnimations ? { opacity: 0, x: -20 } : undefined}
            animate={enableAnimations ? { opacity: 1, x: 0 } : undefined}
            transition={enableAnimations ? { duration: 0.6, delay: 0.2 } : undefined}
          >
            {defaultTitle}
          </motion.span>
        </motion.h1>
        {defaultSubtitle ? (
          <p className="text-lg text-muted-foreground">{defaultSubtitle}</p>
        ) : null}

        <motion.p
          className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto px-4 md:px-0 leading-relaxed font-light"
          initial={enableAnimations ? { opacity: 0, y: 20 } : undefined}
          animate={enableAnimations ? { opacity: 1, y: 0 } : undefined}
          transition={enableAnimations ? { duration: 0.6, delay: 0.6 } : undefined}
        >
          {defaultDescription}
        </motion.p>
      </motion.div>

      {showWizardInstall && (
        <motion.div variants={enableAnimations ? itemVariants : undefined}>
          <WizardInstallSnippet
            variant="hero"
            onCopy={() => {
              trackEvent('homepage_wizard_command_copied', { source: 'hero' });
            }}
            onGuideClick={() => {
              trackEvent('homepage_wizard_guide_clicked', { destination: '/wizard' });
            }}
          />
        </motion.div>
      )}

      {/* CTA Buttons - above hub per UX audit */}
      <motion.div
        variants={enableAnimations ? itemVariants : undefined}
        className="flex flex-col sm:flex-row justify-center gap-4 md:gap-6 px-4 md:px-0"
      >
        <Button
          variant="sanctuary"
          size="lg"
          className="h-12 px-6 md:px-8 text-base md:text-lg font-medium min-w-[200px]"
          onClick={handlePrimaryCTAClick}
        >
          {defaultPrimaryCTA.text}
          <Sparkles className="w-4 h-4 ml-2" aria-hidden />
        </Button>
        <Button
          variant="outline"
          size="lg"
          className="h-12 px-6 md:px-8 text-base md:text-lg font-medium min-w-[200px]"
          onClick={handleSecondaryCTAClick}
        >
          {defaultSecondaryCTA.text}
        </Button>
      </motion.div>

      {/* Integration Display - Central Hub or Traditional Row */}
      {featuredIntegrations.length > 0 && (
        <motion.div
          variants={enableAnimations ? itemVariants : undefined}
          className={showCentralHub ? "w-full max-w-5xl mx-auto" : "flex flex-wrap justify-center gap-6 max-w-4xl mx-auto"}
        >
          {showCentralHub ? (
            <motion.div
              initial={enableAnimations ? { opacity: 0, scale: 0.95 } : undefined}
              animate={enableAnimations ? { opacity: 1, scale: 1 } : undefined}
              transition={enableAnimations ? { duration: 0.8, delay: 0.8 } : undefined}
            >
              <CentralMemoryHub
                integrations={featuredIntegrations}
                maxDisplayed={maxIntegrations}
                onIntegrationClick={handleIntegrationClick}
                enableAnimations={enableAnimations}
                className="py-8"
              />
            </motion.div>
          ) : (
            <motion.div
              initial={enableAnimations ? { opacity: 0 } : undefined}
              animate={enableAnimations ? { opacity: 1 } : undefined}
              transition={enableAnimations ? { duration: 0.6, delay: 0.8, staggerChildren: 0.1 } : undefined}
              className="flex flex-wrap justify-center gap-6"
            >
              {displayedIntegrations.map((integration, index) => (
                <motion.div
                  key={integration.id}
                  initial={enableAnimations ? { opacity: 0, y: 20, scale: 0.9 } : undefined}
                  animate={enableAnimations ? { opacity: 1, y: 0, scale: 1 } : undefined}
                  transition={enableAnimations ? { duration: 0.5, delay: 0.8 + index * 0.1 } : undefined}
                  whileHover={enableAnimations ? {
                    scale: 1.05,
                    transition: { duration: 0.2 }
                  } : undefined}
                  className="flex flex-col items-center space-y-3 p-4 rounded-xl hover:bg-gradient-to-br hover:from-purple-50 hover:to-indigo-50 transition-all duration-300 cursor-pointer group border border-transparent hover:border-purple-200/50 shadow-sm hover:shadow-lg"
                  onClick={() => handleIntegrationClick(integration)}
                >
                  <div className="relative">
                    <motion.div
                      className="w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 group-hover:from-purple-50 group-hover:to-indigo-50 transition-all duration-300 shadow-sm group-hover:shadow-md"
                      whileHover={{ rotate: [0, -10, 10, 0] }}
                      transition={{ duration: 0.4 }}
                    >
                      <IntegrationIcon icon={integration.icon} className="w-7 h-7 text-gray-600 group-hover:text-purple-600 transition-colors" />
                    </motion.div>
                    {(integration as any).isNew && (
                      <div className="absolute -top-1 -right-1 bg-sanctuary text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                        NEW
                      </div>
                    )}
                  </div>
                  <span className="text-sm font-semibold text-gray-700 group-hover:text-purple-700 transition-colors text-center leading-tight max-w-[120px]">
                    {integration.name}
                  </span>
                </motion.div>
              ))}

              {showMoreCount && remainingCount > 0 && (
                <motion.div
                  initial={enableAnimations ? { opacity: 0, scale: 0.8 } : undefined}
                  animate={enableAnimations ? { opacity: 1, scale: 1 } : undefined}
                  transition={enableAnimations ? { duration: 0.4, delay: 0.8 + displayedIntegrations.length * 0.1 } : undefined}
                  className="flex flex-col items-center space-y-3 p-4 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200"
                >
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-white shadow-sm">
                    <span className="text-xl font-bold text-gray-500">+{remainingCount}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-600 text-center leading-tight">
                    {t('hero.more', { ns: 'homepage' })}
                  </span>
                </motion.div>
              )}
            </motion.div>
          )}
        </motion.div>
      )}

      {/* Trust Signal */}
      {defaultTrustSignal && (
        <motion.div
          variants={enableAnimations ? itemVariants : undefined}
          className="text-base text-muted-foreground font-medium bg-white/50 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20 shadow-sm"
          initial={enableAnimations ? { opacity: 0, scale: 0.9 } : undefined}
          animate={enableAnimations ? { opacity: 1, scale: 1 } : undefined}
          transition={enableAnimations ? { duration: 0.5, delay: 1.2 } : undefined}
        >
          {defaultTrustSignal}
        </motion.div>
      )}
    </motion.section>
  );
};

export default HeroSection;
export type { HeroSectionProps, Integration, CTAButton };
